import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, styled, Typography } from '@mui/material';
import { IconDocument } from 'assets/img';
import { FormProvider, useFormContext } from 'react-hook-form';
import { LwFormDate } from 'redesign';
import { DocumentStatus, DocumentType } from '../../table/documents-table.types';
import { getDocumentStatusIcon } from '../../table/utils/get-document-status-icon';
import { getDocumentNameLocisedKey } from '../../utils';

type BodyProps = {
  documentUrl?: string;
  type: DocumentType;
  status: DocumentStatus;
};

export const Body = ({ documentUrl, type, status }: BodyProps) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const methods = useFormContext();

  const handleOpenDocumentPreview = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const key = `documents.status.${status.toLowerCase()}`;

  return (
    <FormProvider {...methods}>
      <FlexColumnBoxWrap>
        <DocumentInfo>
          <DocumentWrap>
            <FlexRowBoxCenter>
              <IconDocument width={130} height={150} onClick={handleOpenDocumentPreview} />
            </FlexRowBoxCenter>
            <ButtonWrapDocument>
              <OpenDocumentButton onClick={handleOpenDocumentPreview} role="button">
                Open
              </OpenDocumentButton>
            </ButtonWrapDocument>
          </DocumentWrap>
          <DocumentInfoWrapper>
            <Stack direction="row" spacing={5} alignItems="center">
              <DocumentName>
                {t(getDocumentNameLocisedKey(type), { ns: 'hq-screen-specific' })}
              </DocumentName>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" noWrap>
                  {t(key, { ns: 'web-common' })}
                </Typography>
                <StyledIcon as={getDocumentStatusIcon(status)} />
              </Box>
            </Stack>
            <DatePickerBox>
              <LwFormDate
                name="contractStartDate"
                label={t('select.start_date', { ns: 'web-common' })}
                control={methods.control}
                rules={{ required: t('select.start_date_error', { ns: 'web-common' }) }}
              />
              <LwFormDate
                name="contractEndDate"
                label={t('select.end_date', { ns: 'web-common' })}
                control={methods.control}
                minDate={methods.watch('contractStartDate')}
                disabled={!methods.watch('contractStartDate')}
              />
            </DatePickerBox>
          </DocumentInfoWrapper>
        </DocumentInfo>
      </FlexColumnBoxWrap>
    </FormProvider>
  );
};

const FlexColumnBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const FlexRowBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const FlexColumnBoxWrap = styled(FlexColumnBox)(() => ({
  overflow: 'auto',
}));

const DocumentInfo = styled(FlexRowBox)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const DocumentWrap = styled(FlexColumnBox)(({ theme }) => ({
  alignItems: 'flex-start',
  marginRight: theme.spacing(8),
}));

const FlexRowBoxCenter = styled(FlexRowBox)(() => ({
  alignItems: 'center',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const ButtonWrapDocument = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
}));

const OpenDocumentButton = styled(Button)(({ theme }) => ({
  '&.MuiButton-root': {
    width: '100%',
    border: '1px solid rgba(0,0,0,0.3)',
    borderRadius: theme.spacing(7),
    padding: '8px 10px',
    fontWeight: 700,
    fontSize: '12px',
    color: 'rgba(0,0,0,0.6)',
  },
}));

const DocumentInfoWrapper = styled(FlexColumnBox)(({ theme }) => ({
  gap: theme.spacing(4),
  width: '100%',
}));

const DocumentName = styled(Typography)(() => ({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '24px',
}));

const DatePickerBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(4),
  display: 'flex',
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(1.5),
}));
